import useAccountFlags from 'hooks/useAccountFlags';
import React, { createContext, useContext } from 'react';
import featuresConfig from 'config/features';
import { isProductionEnv } from 'utils/env';

// All possible feature flags from features.json and API

type UiFlags = typeof featuresConfig[number]['flag'];

type ApiFlags = 'refunds' | 'partialRefunds' | 'subWallets';

export type FeatureFlag = UiFlags | ApiFlags;

interface Feature {
  flag: string;
  description: string;
  enabled:
    | boolean
    | {
        development: boolean;
        production: boolean;
      };
}
type Features = Feature[];

const FeaturesContext = createContext<Features | undefined>(undefined);

export const featuresStorageKey = '__features__';

/**
 * Merge API features with features.json, with API flags overriding JSON flags
 */
function mergeFeatures(
  apiFeatures: Features,
  jsonFeatures: Features
): Features {
  const merged = [...jsonFeatures];

  apiFeatures.forEach((apiFeature) => {
    const existingIndex = merged.findIndex((f) => f.flag === apiFeature.flag);
    if (existingIndex >= 0) {
      merged[existingIndex] = apiFeature;
    } else {
      merged.push(apiFeature);
    }
  });

  return merged;
}

const GROUP_FLAG_OVERRIDES: Record<string, FeatureFlag> = {
  v2_ui: 'v2_flag',
};

/**
 * Turn on any flag the user's groups grant.
 *
 * Runs before composeFeatures so it can still be overriden by localStorage when necessary
 *
 */
function applyGroupOverrides(features: Features, groups: string[]): Features {
  const granted = new Set<string>(
    groups.map((group) => GROUP_FLAG_OVERRIDES[group]).filter(Boolean)
  );

  if (granted.size === 0) {
    return features;
  }

  return features.map((feature) =>
    granted.has(feature.flag) ? { ...feature, enabled: true } : feature
  );
}

/**
 * Features with overrides from localStorage
 */
function composeFeatures(features: Features) {
  try {
    const flagsFromStorage = JSON.parse(
      localStorage.getItem(featuresStorageKey) || '{}'
    );
    return features.map((feature) => {
      const flagFromStorage = flagsFromStorage[feature.flag];
      if (flagFromStorage !== undefined) {
        // Handle both boolean and object enabled shapes
        if (typeof feature.enabled === 'boolean') {
          return {
            ...feature,
            enabled: flagFromStorage,
          };
        } else {
          return {
            ...feature,
            enabled: {
              development: flagFromStorage,
              production: flagFromStorage,
            },
          };
        }
      }
      return feature;
    });
  } catch (err) {
    console.error('Error parsing feature flags from localStorage:', err);
    return features;
  }
}

interface FeaturesProviderProps {
  readonly children: React.ReactNode;
  /**
   * Cognito group names for the signed-in user, from `groupsFromAccess`.
   *
   * A prop rather than a `useAuth()` call on purpose: several test files mount
   * this provider with no `AuthProvider` above it, and it keeps flag
   * composition decoupled from auth.
   */
  readonly groups?: string[];
}

/**
 * API-backed flags arrive asynchronously from `platform/config`. Until they
 * do, every API flag reads as disabled, so anything that *gates navigation* on
 * one has to wait rather than act on that provisional `false`.
 */
const FeaturesLoadingContext = createContext(false);

export function FeaturesProvider({
  children,
  groups = [],
}: FeaturesProviderProps) {
  const { processedFlags, isLoading } = useAccountFlags();

  // Merge API flags with features.json (API overrides JSON on conflicts)
  const mergedFeatures = mergeFeatures(processedFlags || [], [
    ...featuresConfig,
  ]);
  const withGroupOverrides = applyGroupOverrides(mergedFeatures, groups);

  return (
    <FeaturesContext.Provider value={composeFeatures(withGroupOverrides)}>
      <FeaturesLoadingContext.Provider value={isLoading}>
        {children}
      </FeaturesLoadingContext.Provider>
    </FeaturesContext.Provider>
  );
}

/** True while the API-backed feature flags are still being fetched. */
export function useFeaturesLoading() {
  return useContext(FeaturesLoadingContext);
}

function isFeatureEnabled(flag: string, features: Features): boolean {
  const feature = features.find((feature) => feature.flag === flag);
  if (feature) {
    if (typeof feature.enabled === 'boolean') {
      return feature.enabled;
    } else {
      return isProductionEnv
        ? feature.enabled.production
        : feature.enabled.development;
    }
  }
  return false;
}

export function useFeatureContext(flag: FeatureFlag) {
  const context = useContext(FeaturesContext);
  if (context === undefined) {
    throw new Error(
      'useFeaturesContext must be used within a FeaturesProvider'
    );
  }
  return isFeatureEnabled(flag, context);
}
